import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
} from "@mui/material";
import { GlobalButton, GlobalSecondaryButton } from "../../styles";
import WorkRequestInformation from "./WorkRequestInformation";
import "./styles.css";
import WorkRequestPriority from "../../../api/WorkRequest/WorkRequestPriority";
import WorkRequestFacility from "../../../api/WorkRequest/WorkRequestFacility";
import {
  CONTAINERING_METHOD_ENUM,
  RECOMMENDATIONS_ERROR_MESSAGES,
  StyledTextField,
  SubTitle,
  IsInternationalShipping,
  copyWorkRequest,
  sanitizeWorkRequestForCreate,
  defaultErrorsInformation,
  defaultErrorsShippingInformation,
  defaultShippingAddress,
  defaultShippingItem,
  defaultShippingRequest,
  defaultWorkRequest,
  getFacilitiesExternal,
  labelProp,
  statusAllowedForSubmissionForm,
  statusToDisableForm,
  Title,
  WORK_REQUEST_TYPES_ENUM,
  defaultErrorsShippingInformationSiteToSite,
  mappingSiteFacilityExternal,
  getColumnsByRequestType,
  getWorkRequestSampleKey,
  ensureSampleHasUniqueId,
  allowsDuplicateContainers,
} from "./constants";
import ModalSimpleButton from "../../../components/Modal/ModalSimpleButton";
import ShippingInformation from "./ShippingInformation";
import ShippingReason from "../../../api/WorkRequest/ShippingReason";
import ShippingCarrier from "../../../api/WorkRequest/ShippingCarrier";
import AddressBook from "../AddressBook";
import ShippingPrint from "../NewWorkRequests/ShippingPrint";
import ContainersAndTestsProcess from "./ContainersAndTests/ContainersAndTestsProcess";
import UnitOfMeasure from "../../../api/Admin/UnitOfMeasure";
import DOTHazardClass from "../../../api/WorkRequest/DOTHazardClass";
import {
  BuildTestingPayload,
  ConvertUOMs,
  GetRequiredSampleSize,
  GetRequiredSampleSizeUoM,
  convertCelsiusToFahrenheit,
  convertFahrenheitToCelsius,
  convertFlashPoint,
  formatMidasNumber,
  somethingIsDifferent,
  somethingIsDifferentRecursively,
  toUTCDateString,
} from "../../../global";
import { featureToggle } from "../../../utils/featureToggle";
import WorkRequestApi from "../../../api/WorkRequest/WorkRequest";
import TSCAReviewApi from "../../../api/WorkRequest/TSCAReview";
import { useHistory } from "react-router-dom";
import ModalMethodFacilityExternal from "./ModalMethodFacilityExternal";
import WorkRequestStatus from "../../../api/WorkRequest/WorkRequestStatus";
import { usePDF, Document, Page, Text } from "@react-pdf/renderer";
import BillingInfo from "../../../api/Admin/BillingInfo";
import PrintWorkRequest from "./PrintWorkRequest";
import PrintShippingRequest from "./PrintShippingRequest";
import { styles } from "./PrintStylesheet";
import LocationApi from "../../../api/Admin/Location";
import WorkRequestFileAttachment from "../../../api/WorkRequest/WorkRequestFileAttachment";
import WorkRequestType from "../../../api/WorkRequest/WorkRequestType";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Site from "../../../api/Admin/Site";
import WorkRequestTypeSiteMapping from "../../../api/WorkRequest/WorkRequestTypeSiteMapping";
import WorkRequestFiles from "./WorkRequestFiles";
import { useUser } from "../../../context/UserContext";
import { useAuth } from "../../../context/AuthContext";
import { Roles } from "../../../constants/roles";

type TabOptions = "process" | "submit";

type LocationState = {
  tab?: TabOptions;
  myWorkRequest?: any;
};

interface ProcessWorkRequestProps {
  match: any;
  locationState?: LocationState;
}

const drawerWidth = 180;

const ProcessWorkRequest = ({ match, locationState }: ProcessWorkRequestProps) => {
  const workRequestId = match?.params?.id;
  const [workRequestLoading, setWorkRequestLoading] = useState(false);

  const state = locationState;
  const normalizeIdentity = (value?: string | null) => (value || "").trim().toLowerCase();

  const { username } = useUser();
  const { hasAnyRole } = useAuth();
  const isWorkRequestWorker = hasAnyRole([Roles.WorkRequestWorker, Roles.Developer]);

  async function loadWorkRequestData(workRequestToLoad: any) {
    if (workRequestId) {
      if (tabProcess) {
        if (statusToDisableForm.includes(workRequestToLoad.statusName) || !isWorkRequestWorker)
          setDisableForm(true);
        else setDisableForm(false);
      } else {
        if (
          !statusAllowedForSubmissionForm.includes(workRequestToLoad.statusName) ||
          (normalizeIdentity(username) !== normalizeIdentity(workRequestToLoad.requestedByEmail) &&
            normalizeIdentity(username) !== normalizeIdentity(workRequestToLoad.createdByEmail))
        )
          setDisableForm(true);
        else setDisableForm(false);
      }
    }
    setMyWorkRequest(workRequestToLoad);
    checkErrors(workRequestToLoad);
    if (workRequestToLoad.workRequestSamples && workRequestToLoad.workRequestSamples.length > 0) {
      // Ensure all samples have uniqueId (backend id or tempId)
      const samplesWithUniqueId = workRequestToLoad.workRequestSamples.map(ensureSampleHasUniqueId);
      setMySamplesInfo(samplesWithUniqueId);
    }
    if (workRequestToLoad.shippingRequest) {
      const items = workRequestToLoad.shippingRequest.shippingItems;
      const nonChemicalItems = items.filter((f: any) => !f.sampleName && !f.containerID);
      const nonChemicalToSet =
        nonChemicalItems.length > 0 ? nonChemicalItems : [defaultShippingItem];
      setNonChemicalShipping(nonChemicalToSet);
      setNonChemicalFetched(nonChemicalToSet);
      workRequestToLoad.shippingRequest.shippingItems = items
        .filter((f: any) => f.sampleName || f.containerID)
        .map((item: any) => {
          const sampleInfo = workRequestToLoad.workRequestSamples.find(
            (s: any) => s.existingContainerID === item.containerID,
          );
          const sampleKey = sampleInfo ? getWorkRequestSampleKey(sampleInfo) : null;
          const flashPointC = item.flashPointInC
            ? convertFlashPoint(convertCelsiusToFahrenheit, item.flashPointInC) ?? null
            : null;
          return {
            ...item,
            workRequestSampleKey: sampleKey,
            sampleName: sampleInfo?.sample?.sampleName,
            sample: sampleInfo?.sample,
            description: sampleInfo?.sample?.description,
            flashPointInC: flashPointC,
            dotHazardClass: item.dotHazardClass
              ? parseInt(item.dotHazardClass)
              : item.dotHazardClass,
          };
        });
      setShippingRequest(workRequestToLoad.shippingRequest);

      if (workRequestToLoad.shippingRequest.shippingAddress) {
        setShippingAddress(workRequestToLoad.shippingRequest.shippingAddress);
      }
      const isSiteToSite =
        workRequestToLoad.typeName === WORK_REQUEST_TYPES_ENUM.EXXON_MOBIL_SITE_TO_SITE_TRANSPORT;
      setErrorsShipping(
        isSiteToSite
          ? defaultErrorsShippingInformationSiteToSite
          : defaultErrorsShippingInformation,
      );

      checkShippingErrors(
        workRequestToLoad.shippingRequest.shippingAddress,
        workRequestToLoad.shippingRequest,
        isSiteToSite,
      );
    }

    // Carrega os arquivos anexados
    if (workRequestToLoad.id) {
      WorkRequestFileAttachment.getAll(workRequestToLoad.id).then((res) => {
        setFiles(
          res.map((file: any) => {
            return {
              file: {
                ...file,
                name: file.fileName,
              },
            };
          }),
        );
      });
    }
  }

  useEffect(() => {
    if (!username) {
      return;
    }

    async function onWorkRequestLoad(workRequestToLoad: any) {
      await loadWorkRequestData(workRequestToLoad);
    }

    if (workRequestId) {
      setWorkRequestLoading(true);
      WorkRequestApi.getById(workRequestId).then(async (result: any) => {
        if (result) {
          await onWorkRequestLoad(result);
          setWorkRequestFetched(result);
        }
        setWorkRequestLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workRequestId, username]);

  const [workRequestFetched, setWorkRequestFetched] = useState<any>(null);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [requestFacilities, setRequestFacilities] = useState([]);
  const [requestFacilitiesLoading, setRequestFacilitiesLoading] = useState(true);
  const [requestTypes, setRequestTypes] = useState([]);
  const [requestTypesLoading, setRequestTypesLoading] = useState(true);
  // const [businessUnits, setBusinessUnits] = useState([]);
  // const [businessUnitsLoading, setBusinessUnitsLoading] = useState(true);
  const [workRequestPriority, setWorkRequestPriority] = useState("");
  const [workRequestPriorityLoading, setWorkRequestPriorityLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, boolean | null | undefined>>(
    JSON.parse(JSON.stringify(defaultErrorsInformation)),
  );
  const [errorsShipping, setErrorsShipping] = useState<Record<string, boolean | null | undefined>>(
    JSON.parse(JSON.stringify(defaultErrorsShippingInformation)),
  );
  const [myWorkRequest, setMyWorkRequest] = useState<any>({ ...defaultWorkRequest });
  const [modalMessagesOpen, setModalMessagesOpen] = useState(false);
  const [modalMessagesText, setModalMessagesText] = useState("");
  const [modalMessagesTitle, setModalMessagesTitle] = useState("");
  const [modalMessagesButtonText, setModalMessagesButtonText] = useState("Ok");
  const [shippingReasons, setShippingReasons] = useState([]);
  const [shippingReasonsLoading, setShippingReasonsLoading] = useState(true);
  const [shippingCarriers, setShippingCarriers] = useState([]);
  const [shippingCarriersLoading, setShippingCarriersLoading] = useState(true);
  const [addressBookOpen, setAddressBookOpen] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(
    JSON.parse(JSON.stringify(defaultShippingAddress)),
  );
  const [shippingRequest, setShippingRequest] = useState(
    JSON.parse(JSON.stringify(defaultShippingRequest)),
  );
  const [unitOfMeasure, setUnitOfMeasure] = useState([]);
  const [unitOfMeasureWUnit, setUnitOfMeasureWUnit] = useState([]);
  const [unitOfMeasureLoading, setUnitOfMeasureLoading] = useState(true);
  const [mySamplesInfo, setMySamplesInfo] = useState<any[]>([]);
  const [newComment, setNewComment] = useState("");
  const [nonChemicalShipping, setNonChemicalShipping] = useState<any[]>([]);
  const [nonChemicalFetched, setNonChemicalFetched] = useState<any[]>([]);
  const [dotHazardClass, setDOTHazardClass] = useState([]);
  const [dotHazardClassLoading, setDOTHazardClassLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [modalFacilityExternalOpen, setModalFacilityExternalOpen] = useState(false);
  const [facilitiesExternalToShow, setFacilitiesExternalToShow] = useState<any[]>([]);
  const [requestStatus, setRequestStatus] = useState([]);
  const [requestStatusLoading, setRequestStatusLoading] = useState(true);
  const [isRequiredComment, setIsRequiredComment] = useState(false);
  const [billingList, setBillingList] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [files, setFiles] = useState([]);
  const [workRequestTypeSiteMappingList, setWorkRequestTypeSiteMappingList] = useState([]);

  const [disableForm, setDisableForm] = useState(false);

  const history = useHistory();

  const tabProcess = state && state.tab === "process";
  const isProcess =
    tabProcess && myWorkRequest.statusName !== "Draft" && myWorkRequest.id ? true : false;
  const usingShipping =
    somethingIsDifferent(shippingRequest, defaultShippingRequest) ||
    somethingIsDifferent(shippingAddress, defaultShippingAddress) ||
    nonChemicalShipping.some((f) => !!f.description);
  const isShipping = myWorkRequest.typeName === WORK_REQUEST_TYPES_ENUM.SHIPPING;
  const withTests = myWorkRequest.typeName === WORK_REQUEST_TYPES_ENUM.CONTAINER_REGISTRATION_TESTS;
  const isSiteToSiteTransport =
    myWorkRequest.typeName === WORK_REQUEST_TYPES_ENUM.EXXON_MOBIL_SITE_TO_SITE_TRANSPORT;
  const isOnSiteTransport = myWorkRequest.typeName === WORK_REQUEST_TYPES_ENUM.ON_SITE_TRANSPORT;
  const isMiscellaneousRequest =
    myWorkRequest.typeName === WORK_REQUEST_TYPES_ENUM.MISCELLANEOUS_REQUEST;
  const showShipping = isShipping || isSiteToSiteTransport;
  const showContainers = !myWorkRequest.isNonChemical;
  const filteredMySamplesInfo = mySamplesInfo; //filter(mySample => mySample.isRequestContainer);

  const [instance, update] = usePDF({
    document: (
      <PrintWorkRequest
        workRequest={myWorkRequest}
        shippingRequest={shippingRequest}
        shippingAddress={shippingAddress}
        workRequestSamples={filteredMySamplesInfo}
        billingList={billingList}
        methods={[]}
        dotHazardClass={dotHazardClass ?? []}
      />
    ),
  });

  const [instanceShipping, updateShipping] = usePDF({
    document: (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={styles.title}>Request Details - {workRequestId}</Text>
          <PrintShippingRequest
            workRequestSamples={filteredMySamplesInfo}
            shippingRequest={shippingRequest}
            dotHazardClass={dotHazardClass}
            shippingAddress={shippingAddress}
            workRequest={myWorkRequest}
          />
        </Page>
      </Document>
    ),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!workRequestLoading) {
      update(
        <PrintWorkRequest
          workRequest={myWorkRequest}
          shippingRequest={shippingRequest}
          shippingAddress={shippingAddress}
          workRequestSamples={filteredMySamplesInfo}
          billingList={billingList}
          methods={[]}
          dotHazardClass={dotHazardClass ?? []}
        />,
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
      updateShipping(
        <Document>
          <Page size="A4" style={styles.page}>
            <Text style={styles.title}>Request Details - {myWorkRequest.id}</Text>
            <PrintShippingRequest
              workRequestSamples={filteredMySamplesInfo}
              shippingRequest={shippingRequest}
              dotHazardClass={dotHazardClass}
              shippingAddress={shippingAddress}
              workRequest={myWorkRequest}
            />
          </Page>
        </Document>,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workRequestLoading,
    myWorkRequest,
    shippingRequest,
    shippingAddress,
    mySamplesInfo,
    billingList,
    dotHazardClass,
  ]);

  const mountComments = (comments: string[]) => {
    let comment = "";
    comments.forEach((item: string) => {
      comment += item + "\n";
    });
    return comment;
  };

  const hasError = () =>
    Object.entries(errors).some(([key, value]) => value === null || value === true);
  const hasErrorShipping = () =>
    usingShipping &&
    Object.entries(errorsShipping).some(([key, value]) => value === null || value === true);
  const hasErrorMethods = () => {
    const parsedValue = JSON.parse(myWorkRequest.testSubmissionJSON);
    const methodError = parsedValue?.methods?.some(
      (method: any) =>
        method?.methodFacility?.requiresConditions === true && !method?.conditionOffering,
    );

    return methodError;
  };

  function updateErrors() {
    setErrors((prevErrors: any) => {
      const updatedErrors = Object.keys(prevErrors).reduce((acc: any, key) => {
        acc[key] = prevErrors[key] === null ? true : prevErrors[key];
        return acc;
      }, {});

      return updatedErrors;
    });
  }

  function updateErrorsShipping() {
    setErrorsShipping((prevErrors: any) => {
      const updatedErrors = Object.keys(prevErrors).reduce((acc: any, key) => {
        acc[key] = !usingShipping ? false : prevErrors[key] === null ? true : prevErrors[key];
        return acc;
      }, {});

      return updatedErrors;
    });
  }

  const errorsToString = Object.entries(errors)
    .filter(([key, value]) => value === null || value === true)
    .map(([key]) => labelProp[key])
    .join("\n");
  const errorsToStringShipping = Object.entries(errorsShipping)
    .filter(([key, value]) => value === null || value === true)
    .map(([key]) => labelProp[key])
    .join("\n");
  const nonChemicalsWithError = nonChemicalShipping.filter(
    (x) => x.description && x.description !== "" && (!x.quantity || x.quantity < 1),
  );
  const sampleWithoutCointainer = mySamplesInfo.filter((f) => !f.existingContainerID);
  const sampleWithoutFinalLocation = mySamplesInfo.filter(
    (f) => !f.requestedLocationName || f.requestedLocationName === "",
  );

  useEffect(() => {
    let cancelPromise = false;

    Promise.all([
      Site.getAllActive().then((res) => {
        if (cancelPromise) return;
        setSitesLoading(false);
        setSites(res.sort((a: any, b: any) => a.siteName.localeCompare(b.siteName)));
      }),
      WorkRequestFacility.getAllActive().then((res) => {
        if (cancelPromise) return;
        setRequestFacilitiesLoading(false);
        setRequestFacilities(
          res.sort((a: any, b: any) => a.facilityName.localeCompare(b.facilityName)),
        );
      }),
      WorkRequestPriority.getAll()
        .then((res) => {
          setWorkRequestPriority(
            res.map((priority: any) => ({
              ...priority,
              priority: priority.priorityName,
            })),
          );
          setWorkRequestPriorityLoading(false);
        })
        .catch(() => {
          setWorkRequestPriorityLoading(false);
        }),
      WorkRequestType.getAllActive().then((res) => {
        if (cancelPromise) return;
        setRequestTypesLoading(false);
        setRequestTypes(res.sort((a: any, b: any) => a.typeName.localeCompare(b.typeName)));
      }),
      // WorkRequestBusinessUnit.getAllActive().then((res) => {
      //   if (cancelPromise) return;
      //   setBusinessUnitsLoading(false);
      //   setBusinessUnits(
      //     res.sort((a: any, b: any) =>
      //       a.businessUnitName.localeCompare(b.businessUnitName)
      //     )
      //   );
      // }),
      ShippingReason.getAllActive().then((res) => {
        if (cancelPromise) return;
        setShippingReasonsLoading(false);
        setShippingReasons(
          res.sort((a: any, b: any) => a.shippingReasonName.localeCompare(b.shippingReasonName)),
        );
      }),

      ShippingCarrier.getAllActive().then((res) => {
        if (cancelPromise) return;
        setShippingCarriersLoading(false);
        setShippingCarriers(
          res
            .map((carrier: any) => ({
              ...carrier,
              shippingCarrierID: carrier.id,
            }))
            .sort((a: any, b: any) => a.carrierName.localeCompare(b.carrierName)),
        );
      }),
      UnitOfMeasure.getAllActive().then((res) => {
        if (cancelPromise) return;
        setUnitOfMeasureLoading(false);
        setUnitOfMeasureWUnit(
          res
            .filter(
              (result: any) =>
                result.type === "weight" || result.type === "volume" || result.type === "unit",
            )
            .sort((a: any, b: any) => a.uoMName.localeCompare(b.uoMName)),
        );
        setUnitOfMeasure(
          res
            .filter(
              (result: any) =>
                (result.type === "weight" || result.type === "volume") &&
                result.metricStandardConversion !== null,
            )
            .sort((a: any, b: any) => a.uoMName.localeCompare(b.uoMName)),
        );
      }),
      DOTHazardClass.getAllActive().then((res: any) => {
        if (cancelPromise) return;
        setDOTHazardClassLoading(false);
        setDOTHazardClass(res.sort((a: any, b: any) => a.displayName.localeCompare(b.displayName)));
      }),
      WorkRequestStatus.getAll().then((res: any) => {
        if (cancelPromise) return;
        setRequestStatusLoading(false);
        const statuses = res
          .filter(
            (result: any) =>
              result.isActive === true &&
              result.statusName !== "Draft" &&
              result.statusName !== "Deleted",
          )
          .sort((a: any, b: any) => a.statusName.localeCompare(b.statusName));
        const hasOnHold = statuses.some(
          (s: any) =>
            (s?.statusName || "")
              .trim()
              .toLowerCase()
              .replace(/[\s_-]/g, "") === "onhold",
        );
        if (!hasOnHold) statuses.push({ statusName: "OnHold", isActive: true });
        setRequestStatus(
          statuses.sort((a: any, b: any) => a.statusName.localeCompare(b.statusName)),
        );
      }),
      BillingInfo.getAll().then((res: any) => {
        if (res.message === "Success") {
          setBillingList(res.result.filter((result: any) => result.isActive === true));
        }
      }),
      LocationApi.getAll().then((res) => {
        if (cancelPromise) return;
        setAvailableLocations(
          res
            .filter((result: any) => result.isActive === true)
            .sort((a: any, b: any) => a.locationName.localeCompare(b.locationName)),
        );
      }),
      WorkRequestTypeSiteMapping.getAll().then((res: any) => {
        if (cancelPromise) return;
        setWorkRequestTypeSiteMappingList(res ?? []);
      }),
    ]).then(() => {});

    return () => {
      cancelPromise = true;
    };
  }, []);

  async function handleWorkRequestUpdate(workRequestIdToUpdate: number) {
    const updatedWorkRequest = await WorkRequestApi.getById(workRequestIdToUpdate);
    if (updatedWorkRequest) {
      setWorkRequestFetched(updatedWorkRequest);
      await loadWorkRequestData(updatedWorkRequest);
    }
  }

  function openModalMethodFacilityExternal(result: any) {
    const facilitiesExternal = getFacilitiesExternal(mySamplesInfo) ?? [];
    const facilitiesToShow: any[] = [];
    facilitiesExternal.forEach((facility: any) => {
      const workRequest = result.find((f: any) => {
        const sample = f.workRequestSamples[0];
        // if (!sample?.testSubmissionJSON) return false;
        // const parsedValue = JSON.parse(sample.testSubmissionJSON);
        return (
          sample?.sampleName === facility.sampleName &&
          sample?.existingContainerID === facility.existingContainerID
        ); //&& parsedValue.methods.some((s: any) => facility?.method?.includes(s.method.name))
      });
      if (workRequest?.id) {
        const address = workRequest.shippingRequest.shippingAddress;
        facilitiesToShow.push({
          ...facility,
          workRequestId: workRequest?.id,
          address: address ? `${address?.address1} ${address?.city}, ${address?.state}` : "N/A",
          statusName: workRequest.statusName,
        });
      }
    });
    setFacilitiesExternalToShow(facilitiesToShow);
    setModalFacilityExternalOpen(true);
  }

  function closeModalFacilityExternal() {
    setModalFacilityExternalOpen(false);
    setFacilitiesExternalToShow([]);
  }

  function openModalMessages(title: string, text: string) {
    setModalMessagesOpen(true);
    setModalMessagesTitle(title);
    setModalMessagesText(text);
    setModalMessagesButtonText("Ok");
  }

  function closeModalMessages() {
    setModalMessagesOpen(false);
    setModalMessagesText("");
    setModalMessagesButtonText("");
    setModalMessagesTitle("");
  }

  // const mySamplesRequired = mySamplesInfo.length < 1 && !myWorkRequest.isNonChemical;

  // eslint-disable-next-line
  async function payloadToGetRecommendation(methods: any, workRequestSamples: any) {
    const errosArray = checkAmountAvailable(methods, workRequestSamples);
    if (errosArray.length > 0) {
      openModalMessages(
        "Container Amount Error",
        `${RECOMMENDATIONS_ERROR_MESSAGES.INVALID_AMOUNT}\n${errosArray.join("\n")}`,
      );
      return;
    }

    let newTests = [];
    if (methods.length > 0) {
      for (const oMethodInfo of methods) {
        const tests = await BuildTestingPayload(
          oMethodInfo,
          null,
          null,
          myWorkRequest.chargeCode,
          username,
        );

        if (tests === null || typeof tests === "string" || typeof tests[0] === "string") {
          if (Array.isArray(tests)) {
            openModalMessages("Error Building Tests", "");
            return;
          } else {
            openModalMessages(
              "Error Building Tests",
              `There was an error building the test payload. ${tests ? tests : ""}`,
            );
            return;
          }
        }
        for (const oTest of tests) {
          newTests.push(oTest);
        }
      }
    }
    return {
      containeringMethod: CONTAINERING_METHOD_ENUM.MIDAS,
      requireParentContainer: false,
      tests: newTests,
    };
  }

  function checkAmountAvailable(methods: any, workRequestSamples: any) {
    let containerAmtError: string[] = [];

    workRequestSamples.forEach((sample: any) => {
      // const currentContainer = sample.sample.containers.find(
      //   (x: any) => x.id === sample.existingContainerID
      // );
      const currentContainer = sample;
      const amtNeededForTest = methods.reduce((acc: any, currentMethod: any) => {
        let convertedValue = ConvertUOMs(
          sample.sample,
          sample.sample.substance,
          GetRequiredSampleSize(currentMethod),
          unitOfMeasure.find((x: any) => x.uoMName === GetRequiredSampleSizeUoM(currentMethod)),
          currentContainer?.uom ?? null,
        );
        return acc + convertedValue;
      }, 0);

      if (currentContainer?.currentAmount < amtNeededForTest) {
        containerAmtError.push(
          `Container ${currentContainer.containerNumber} of Sample ${formatMidasNumber(
            sample.sample.sampleName,
          )} does not have enough volume for all tests. Amount needed: ${amtNeededForTest.toFixed(
            2,
          )} ${
            currentContainer.containerSizeUoM
          } - Container amount: ${currentContainer.currentAmount.toFixed(2)} ${
            currentContainer.containerSizeUoM
          }`,
        );
      }
    });
    return containerAmtError;
  }

  const prepareData = async (
    workRequest: any,
    draft: boolean,
    keepNewContainer: boolean = false,
    isSubmission: boolean = false,
  ) => {
    const [workRequestSamples, workRequestSamplesWithExternal, newContainers] = prepareDataSamples(
      !draft && isSubmission,
    );
    // let testingContainers = null;

    // if (workRequestSamples.length > 0) {
    //   const methods = workRequestSamples.filter(f => f.testSubmissionJSON && f.testSubmissionJSON !== '').flatMap((samples: any) => (JSON.parse(samples.testSubmissionJSON))?.methods);
    //   testingContainers = payloadToGetRecommendation(methods, workRequestSamples);
    //   if (!testingContainers && methods.length > 0) return;
    // }

    const workRequestFinally = {
      ...workRequest,
      requestedCompletionDate: toUTCDateString(workRequest.requestedCompletionDate),
      submissionDate: draft ? null : isSubmission ? new Date() : workRequest.submissionDate,
      statusName: draft ? "Draft" : isSubmission ? "Submitted" : workRequest.statusName,
      testSubmissionJSON:
        isSubmission && workRequestSamplesWithExternal?.length > 0 && workRequestSamples?.length > 0
          ? workRequestSamples[0].testSubmissionJSON
          : workRequest.testSubmissionJSON,
      // facilityName: !shippingRequest?.isTSCAReview ? workRequest.facilityName : isSubmission ? 'EMBSI' : workRequest.facilityName,
      // typeName: usingShipping ? 'Shipping' : workRequestSamples.some((s: any) => {
      //   if (s.testSubmissionJSON && s.testSubmissionJSON !== '') {
      //     const parsed = JSON.parse(s.testSubmissionJSON)
      //     return parsed?.methods?.length > 0;
      //   }

      //   return false;
      // }) ? 'Container Registration w/ Tests' : workRequestSamples.length > 0 ? workRequestSamples.some((s: any) => s.requestedLocationName && s.requestedLocationName !== '')
      //   ? 'On-site Transport' : 'Container Registration' : 'Miscellaneous Request',
      workRequestSamples: workRequestSamples
        // isRequestContainer === false only exists to carry a newContainerID, so a row without
        // a created or pending container would be persisted as garbage.
        .filter(
          (sample: any) =>
            sample?.isRequestContainer !== false ||
            sample?.newContainerID > 0 ||
            (keepNewContainer && sample?.newContainer),
        )
        .map((sample: any) => {
        // let currentContainer = sample.sample.containers.find(
        //   (x: any) => x.id === sample.existingContainerID
        // );
        if (!keepNewContainer) delete sample.newContainer;
        // Remove tempId before sending to backend
        delete sample.tempId;
        return {
          ...sample,
          requestedAmount: sample.requestedAmount,
          requestedAmountUoM: sample.requestedAmountUoM,
          ...(keepNewContainer && sample.newContainer
            ? {
                newContainer: {
                  ...sample.newContainer,
                  tests: sample.newContainer.tests?.map((test: any) => ({
                    ...test,
                    chargeCode: workRequest.chargeCode,
                  })),
                },
              }
            : {}),
          sample: null,
          sampleName: sample.sample?.sampleName,
          unitOfMeasure: null,
          location: null,
          container: null,
          existingContainer: null,
        };
      }),
      shippingRequest: usingShipping ? prepareDataShipping() : null,
    };

    const groupedSamplesExternalByLaboratory: Record<string, any> = {};
    const shouldAllowDuplicateContainers = allowsDuplicateContainers(workRequestFinally.typeName);

    workRequestSamplesWithExternal.forEach((sampleExternal: any) => {
      delete sampleExternal.id;
      delete sampleExternal.workRequestID;
      // let currentContainer = sampleExternal?.sample?.containers.find(
      //   (x: any) => x.id === sampleExternal.existingContainerID
      // );
      let currentContainer = sampleExternal?.sample;

      const externalLaboratory = sampleExternal?.externalLaboratory?.laboratoryName;
      const addressExternalLaboratory = sampleExternal?.externalLaboratory?.shippingAddress;
      const shippingAddressExternal = addressExternalLaboratory
        ? {
            city: addressExternalLaboratory.city,
            state: addressExternalLaboratory.state,
            postalCode: addressExternalLaboratory.postalCode,
            country: addressExternalLaboratory.country,
            phoneNumber1: addressExternalLaboratory.phoneNumber1,
            emailAddress: addressExternalLaboratory.emailAddress,
            recipientName: addressExternalLaboratory.recipientName,
            address1: addressExternalLaboratory.address1,
            company: addressExternalLaboratory.company,
            department: addressExternalLaboratory.department,
          }
        : null;

      if (!groupedSamplesExternalByLaboratory[externalLaboratory]) {
        const facilityMapping = mappingSiteFacilityExternal[workRequestFinally.siteName];
        groupedSamplesExternalByLaboratory[externalLaboratory] = {
          ...workRequestFinally,
          shippingRequest: workRequestFinally.shippingRequest
            ? {
                ...workRequestFinally.shippingRequest,
                shippingItems: workRequestFinally.shippingRequest?.shippingItems,
                shippingAddress: shippingAddressExternal,
              }
            : shippingAddressExternal
            ? {
                ...defaultShippingRequest,
                shippingAddress: shippingAddressExternal,
              }
            : null,
          statusName: "Draft",
          typeName: WORK_REQUEST_TYPES_ENUM.SHIPPING,
          workRequestSamples: [],
          testSubmissionJSON: null,
          facilityName:
            facilityMapping &&
            requestFacilities.some((f: any) => f.facilityName === facilityMapping)
              ? facilityMapping
              : null,
        };
      }

      const workRequestSample = shouldAllowDuplicateContainers
        ? null
        : groupedSamplesExternalByLaboratory[externalLaboratory]?.workRequestSamples.find(
            (s: any) =>
              s.sampleName === sampleExternal.sample.sampleName &&
              sampleExternal.existingContainerID === s.existingContainerID,
          );

      if (workRequestSample) {
        const parsedValue = JSON.parse(workRequestSample.testSubmissionJSON);
        const sampleExternalParsedValue = JSON.parse(sampleExternal.testSubmissionJSON);

        sampleExternalParsedValue?.methods?.forEach((method: any) => {
          const key = `${method.method.name}-${method.methodFacility.testFacilityAbv}`;

          if (
            !parsedValue?.methods?.some(
              (s: any) => `${s.method.name}-${s.methodFacility.testFacilityAbv}` === key,
            )
          )
            parsedValue.methods.push(method);
        });
        // workRequestSample.testSubmissionJSON = JSON.stringify(parsedValue);
        workRequestSample.testSubmissionJSON = null;
        workRequestSample.newContainer.tests = workRequestSample.newContainer.tests.map(
          (test: any) => ({ ...test, chargeCode: workRequest.chargeCode }),
        );
      } else {
        groupedSamplesExternalByLaboratory[externalLaboratory]?.workRequestSamples.push({
          ...sampleExternal,
          requestedAmount: sampleExternal.requestedAmount ?? currentContainer.currentAmount,
          requestedAmountUoM:
            sampleExternal.requestedAmountUoM ?? currentContainer.containerSizeUoM,
          sample: null,
          sampleName: sampleExternal.sample?.sampleName,
          unitOfMeasure: null,
          location: null,
          container: null,
          existingContainer: null,
          testSubmissionJSON: null,
        });
      }
    });

    return [
      {
        ...workRequestFinally,
        shippingRequest: workRequestFinally.shippingRequest
          ? {
              ...workRequestFinally.shippingRequest,
              shippingItems: workRequestFinally.shippingRequest?.shippingItems?.filter(
                (f: any) =>
                  workRequestFinally.workRequestSamples.some(
                    (s: any) =>
                      f.containerID === s.existingContainerID && f.sampleName === s?.sampleName,
                  ) ||
                  (f.description && f.description !== ""),
              ),
            }
          : null,
      },
      Object.values(groupedSamplesExternalByLaboratory),
      newContainers,
    ];
  };

  const prepareDataShipping = () => {
    let chemShipping = structuredClone(shippingRequest).shippingItems;

    chemShipping.forEach((x: any, index: any, array: any) => {
      const flashPoint = x.flashPointInC
        ? convertFlashPoint(convertFahrenheitToCelsius, x.flashPointInC) ?? null
        : null;
      array[index].flashPointInC = flashPoint;
      array[index].sample = null;
      // Remove workRequestSampleKey before sending to backend
      delete array[index].workRequestSampleKey;
    });

    return {
      ...shippingRequest,
      shippingAddress: shippingAddress,
      printBlindCode: shippingRequest.shippingItems.some(
        (s: any) => s.blindCodeDescription && s.blindCodeDescription !== "",
      ),
      numberOfItems: shippingRequest.shippingItems.length,
      shippingItems: [...chemShipping, ...nonChemicalShipping.filter((x) => !!x.description)],
    };
  };

  const prepareDataSamples = (separateFacilityExternal: boolean): any => {
    const samples: any[] = [];
    const samplesWithExternal: any[] = [];
    // const sampleWithoutCointainer = mySamplesInfo.filter(f => f.containers.length < 1);
    const samplesFlat = mySamplesInfo.filter(
      (f) =>
        (f.sampleName || f.sample) &&
        (f?.isRequestContainer !== false || f?.newContainerID > 0 || f?.newContainer),
    );
    // if (sampleWithoutCointainer && myWorkRequest.hasContainer) {
    //   sampleWithoutCointainer.forEach(sample => {
    //     samplesFlat.push({
    //       ...samples,
    //       sample: sample?.sample
    //     })
    //   })
    // }
    const newContainers = samplesFlat
      .flatMap((samples: any) => samples.newContainers)
      .filter((f) => f);
    const haveSampleWithoutMethods = samplesFlat.some(
      (f) => !f.testSubmissionJSON || f.testSubmissionJSON === "",
    );
    let countExternal = 0;
    const everyFacilityExternal = samplesFlat.every((sample: any) => {
      if (!sample.testSubmissionJSON) return false;
      const parsedValue = JSON.parse(sample.testSubmissionJSON);
      const facilitiesExternal =
        parsedValue.methods.filter((m: any) => m.externalLaboratory).length ?? 0;
      countExternal += facilitiesExternal;
      return parsedValue.methods.every((m: any) => m.externalLaboratory) && facilitiesExternal > 0;
    });

    if (!separateFacilityExternal) return [samplesFlat, [], newContainers ?? []];
    if (samplesFlat.length === 1 && everyFacilityExternal && countExternal === 1) {
      samplesFlat.push(samplesFlat[0]);
    }

    if (!haveSampleWithoutMethods && everyFacilityExternal) {
      samplesFlat.forEach((sample) => {
        if (sample.testSubmissionJSON) {
          const parsedValue = JSON.parse(sample.testSubmissionJSON);
          const methodsExternal = parsedValue.methods.filter((m: any) => m.externalLaboratory);

          if (methodsExternal.length > 0) {
            methodsExternal.forEach((external: any, index: number) => {
              parsedValue.methods = [external];
              if (index === 0 && samples.length === 0) {
                samples.push({
                  ...sample,
                  testSubmissionJSON: JSON.stringify(parsedValue),
                  externalLaboratory: external.externalLaboratory,
                });
              } else {
                samplesWithExternal.push({
                  ...sample,
                  testSubmissionJSON: JSON.stringify(parsedValue),
                  externalLaboratory: external.externalLaboratory,
                });
              }
            });
          }
        }
      });
    } else {
      samplesFlat.forEach((sample) => {
        if (sample.testSubmissionJSON) {
          const parsedValue = JSON.parse(sample.testSubmissionJSON);
          const methodsExternal = parsedValue.methods.filter((m: any) => m.externalLaboratory);
          parsedValue.methods = parsedValue.methods.filter((m: any) => !m.externalLaboratory);

          if (parsedValue.methods.length > 0 || methodsExternal.length === 0) {
            samples.push({
              ...sample,
              testSubmissionJSON: JSON.stringify(parsedValue),
            });
          }

          if (methodsExternal.length > 0) {
            methodsExternal.forEach((external: any) => {
              parsedValue.methods = [external];
              samplesWithExternal.push({
                ...sample,
                testSubmissionJSON: JSON.stringify(parsedValue),
                externalLaboratory: external.externalLaboratory,
              });
            });
          }
        } else {
          samples.push({
            ...sample,
          });
        }
      });
    }
    return [samples, samplesWithExternal, newContainers ?? []];
  };

  async function createTscaRequest(workRequest: any) {
    if (
      workRequest.shippingRequest !== null &&
      workRequest.shippingRequest.isTSCAReview &&
      workRequest.statusName !== "Draft"
    ) {
      let response = await TSCAReviewApi.createTSCAReview({
        shippingRequestID: workRequest?.shippingRequest?.id,
        tscaStatusName: "Submitted",
      })
        .then((res) => true)
        .catch((err) => false);

      return response;
    }

    return true;
  }

  async function sendFiles(id: any) {
    const filesToCreate = files.filter((f: any) => !f.file?.id);
    const filesToUpdate = files.filter((f: any) => f.file?.id);
    let resultCreate = [];
    let resultUpdate = [];

    if (filesToCreate.length < 1 && filesToUpdate.length < 1) return;

    if (filesToCreate.length > 0) {
      resultCreate = await Promise.all(
        filesToCreate.map(async (file: any, index) => {
          const formData = new FormData();
          formData.append("file", file.file);
          formData.append("workRequestID", id);
          formData.append("sendWithShipment", file.sendWithShipment);
          formData.append("isSDS", file.isSDS);

          let resp = await WorkRequestFileAttachment.postAttachs(formData);

          return resp;
        }),
      );
    }
    if (filesToUpdate.length > 0) {
      resultUpdate = await Promise.all(
        filesToUpdate.map(async (file: any, index) => {
          let resp = await WorkRequestFileAttachment.updateWorkRequest(file.file);

          return resp;
        }),
      );
    }
    if (resultCreate?.length || resultUpdate?.length) {
      if (resultCreate && !resultCreate[0]?.result && resultCreate[0]?.message) {
        throw new Error(resultCreate[0].message);
      }

      return true;
    } else return false;
  }

  async function submit() {
    if (isOnSiteTransport && sampleWithoutFinalLocation.length > 0) {
      const itemsWithError = sampleWithoutFinalLocation
        .map((item) => `MIDAS # ${formatMidasNumber(item.sample?.sampleName)}`)
        .join("\n");
      openModalMessages(
        "",
        "Final Location must be provided for On-Site Transport:\n\n" + itemsWithError,
      );
      return;
    }

    const isChangingToCompleted = myWorkRequest.statusName === "Completed";

    if (isChangingToCompleted) {
      const areFieldsInvalid = validateFieldsSamples();
      if (areFieldsInvalid.hasError && areFieldsInvalid.fields.length > 0) {
        const fields = areFieldsInvalid.fields.join("\n");
        openModalMessages("Missing information!", fields);
        return;
      }

      const columns = getColumnsByRequestType(myWorkRequest.typeName, true);
      if (columns.includes("requestedAmount")) {
        const samplesWithInvalidAmount = mySamplesInfo.filter(
          (sample: any) =>
            (!sample.requestedAmount || (sample.requestedAmount && sample.requestedAmount <= 0)) &&
            sample.sample,
        );

        if (samplesWithInvalidAmount.length > 0) {
          const invalidSamples = samplesWithInvalidAmount
            .map((sample: any) => `MIDAS # ${formatMidasNumber(sample.sample?.sampleName)}`)
            .join("\n");

          openModalMessages(
            "Invalid Requested Amount",
            `The following samples have invalid requested amount (must be greater than 0):\n\n${invalidSamples}`,
          );
          return;
        }
      }

      if (isRequiredComment) {
        openModalMessages("Missing information!", "A comment is required. Please Add one.");
        return;
      }

      if (hasError()) {
        updateErrors();

        openModalMessages(
          "Missing information!",
          "There are required fields missing:\n\n" + errorsToString,
        );
        return;
      }

      if (hasErrorShipping()) {
        updateErrorsShipping();

        openModalMessages(
          "Missing information!",
          "There are required fields missing in Shipping:\n\n" + errorsToStringShipping,
        );
        return;
      }

      if (hasErrorMethods()) {
        updateErrorsShipping();

        openModalMessages("Missing information!", "There are required fields missing in Methods");
        return;
      }

      if (showShipping && myWorkRequest.isNonChemical && nonChemicalsWithError.length > 0) {
        const itemsWithError = nonChemicalsWithError.map((item) => item.description).join("\n");
        openModalMessages(
          "",
          "Quantity for non-chemical items must be provided:\n\n" + itemsWithError,
        );
        return;
      }

      if (isOnSiteTransport && sampleWithoutCointainer.length > 0) {
        const itemsWithError = sampleWithoutCointainer
          .map((item) => `MIDAS # ${formatMidasNumber(item.sample?.sampleName)}`)
          .join("\n");
        openModalMessages("", "Container must be provided:\n\n" + itemsWithError);
        return;
      }
    }

    setLoadingSubmit(true);
    const isSubmission = myWorkRequest.statusName === "Draft" ? true : false;
    const [data, datasExternal] = await prepareData(myWorkRequest, false, false, isSubmission);

    const api =
      data.id && data.id > 0
        ? WorkRequestApi.newUpdateWorkRequest
        : WorkRequestApi.newCreateWorkRequest;
    api(data)
      .then(async (res) => {
        if (res?.message !== "Success") {
          openModalMessages(
            "Fail",
            `Something went wrong saving the Work Requests, please try again later. ${
              res?.message ? res?.message : ""
            }`,
          );
          setLoadingSubmit(false);
        } else {
          const workRequest = res.result;
          if (isSubmission) await createTscaRequest(workRequest);
          await sendFiles(workRequest.id);

          if (datasExternal && datasExternal.length > 0) {
            const resultExternals = await Promise.all(
              datasExternal.map(async (external: any) => {
                delete external.id;
                return await WorkRequestApi.newCreateWorkRequest({
                  ...external,
                  parentWorkRequestID: workRequest.id,
                });
              }),
            );

            if (resultExternals.every((e) => e.message === "Success")) {
              await handleWorkRequestUpdate(workRequest.id);
              openModalMethodFacilityExternal(resultExternals.flatMap((f) => f.result));
            } else {
              await handleWorkRequestUpdate(workRequest.id);
              openModalMessages(
                "Fail",
                `Work Request Saved Successfully, but something went wrong saving the Work Requests for External Facilities, please try again later. ${
                  res?.message ? res?.message : ""
                }`,
              );
            }
          } else {
            await handleWorkRequestUpdate(workRequest.id);
            openModalMessages("Work Request Saved", "Work Request Saved Successfully");
          }

          setLoadingSubmit(false);
        }
      })
      .catch((err) => {
        setLoadingSubmit(false);
        openModalMessages(
          "Fail",
          `Something went wrong saving the Work Requests, please try again later. ${
            err?.message ? err?.message : ""
          }`,
        );
      });
  }

  // function createContainerWithTests(newContainers: any) {
  //   Container.CreateContainersWithTests(newContainers);
  // }

  function handleAddressBook(addressBook: any) {
    if (addressBook) {
      setShippingAddress(() => ({
        ...shippingAddress,
        city: addressBook.city,
        state: addressBook.state,
        postalCode: addressBook.postalCode,
        country: addressBook.country,
        phoneNumber1: addressBook.phoneNumber1,
        emailAddress: addressBook.emailAddress,
        recipientName: addressBook.recipientName,
        address1: addressBook.address1,
        company: addressBook.company,
        department: addressBook.department,
      }));

      const newShippingRequest = shippingRequest;
      if (IsInternationalShipping(addressBook)) {
        newShippingRequest.isTSCAReview = true;
        setShippingRequest(newShippingRequest);
      }

      checkShippingErrors(addressBook, newShippingRequest, isSiteToSiteTransport);
    }
  }

  function checkErrors(workRequestObject: any) {
    const error: any = JSON.parse(JSON.stringify(defaultErrorsInformation));
    Object.keys(defaultErrorsInformation).forEach((key: string) => {
      if (workRequestObject.hasOwnProperty(key)) {
        const object = workRequestObject as any;
        error[key] = !object[key] || object[key] === "" ? true : false;
      }
    });
    setErrors((prevErrors) => ({
      ...prevErrors,
      ...error,
    }));
  }

  function checkShippingErrors(addressObject: any, shippingObject: any, siteToSite: boolean) {
    const shippingError: any = JSON.parse(
      JSON.stringify(
        siteToSite ? defaultErrorsShippingInformationSiteToSite : defaultErrorsShippingInformation,
      ),
    );

    Object.keys(
      siteToSite ? defaultErrorsShippingInformationSiteToSite : defaultErrorsShippingInformation,
    ).forEach((key: string) => {
      if (addressObject.hasOwnProperty(key)) {
        const object = addressObject as any;
        shippingError[key] = !object[key] || object[key] === "" ? true : false;
      } else if (shippingObject.hasOwnProperty(key)) {
        const request = shippingObject as any;
        shippingError[key] =
          request[key] === undefined || request[key] === null || request[key] === "" ? true : false;
      }
    });
    setErrorsShipping((prevErrors) => ({
      ...prevErrors,
      ...shippingError,
    }));
  }

  function hasUnsavedChanges() {
    if (!workRequestFetched) return false;

    const fetched = workRequestFetched as any;

    // Check if work request has changes (deep comparison)
    const workRequestChanged = somethingIsDifferentRecursively(myWorkRequest, fetched);

    // Check if shipping information has changes
    const shippingRequestFetched = fetched.shippingRequest || defaultShippingRequest;
    const shippingAddressFetched =
      fetched.shippingRequest?.shippingAddress || defaultShippingAddress;
    const shippingChanged =
      somethingIsDifferentRecursively(shippingRequest, shippingRequestFetched) ||
      somethingIsDifferentRecursively(shippingAddress, shippingAddressFetched);

    // Check if non-chemical shipping items have changed
    const nonChemicalChanged = somethingIsDifferentRecursively(
      nonChemicalShipping,
      nonChemicalFetched,
    );

    // Check if samples have changed
    const samplesChanged = somethingIsDifferentRecursively(
      mySamplesInfo,
      fetched.workRequestSamples || [],
      ["testSubmissionJSON"],
    );

    return workRequestChanged || shippingChanged || nonChemicalChanged || samplesChanged;
  }

  function printRequest() {
    if (hasUnsavedChanges()) {
      setModalMessagesTitle("Unsaved Changes");
      setModalMessagesText("You need to save changes before printing the Work Request.");
      setModalMessagesButtonText("Ok");
      setModalMessagesOpen(true);
      return;
    }

    if (instance && !instance.loading && !instance.error && instance.url) {
      const link = document.createElement("a");
      link.href = instance.url;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  const handlePrintShippingForm = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    if (hasUnsavedChanges()) {
      setModalMessagesTitle("Unsaved Changes");
      setModalMessagesText("You need to save changes before printing the Shipping Form.");
      setModalMessagesButtonText("Ok");
      setModalMessagesOpen(true);
      return;
    }

    if (
      instanceShipping &&
      !instanceShipping.loading &&
      !instanceShipping.error &&
      instanceShipping.url
    ) {
      const link = document.createElement("a");
      link.href = instanceShipping.url;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const validateFieldsSamples = () => {
    let fieldsInvalidList: string[] = [];
    const workRequestSamples = mySamplesInfo.filter((wrs: any) => wrs.sample);

    const fieldsRequired = [
      {
        field: "existingContainerID",
        name: "Container",
      },
    ];
    workRequestSamples.forEach((sample: any) => {
      fieldsRequired.forEach((field) => {
        if (
          field.field === "existingContainerID" &&
          sample.newContainerID &&
          sample.newContainerID > 0
        ) {
          return;
        }

        if ((!sample[field.field] && sample[field.field] !== 0) || sample[field.field] === "") {
          fieldsInvalidList.push(
            `${formatMidasNumber(sample?.sample?.sampleName)}: ${field.name} is required`,
          );
        }
      });
    });

    return {
      hasError: fieldsInvalidList.length > 0,
      fields: fieldsInvalidList,
    };
  };

  const addCommentComponent = () => {
    return (
      <>
        <SubTitle>Add Comment</SubTitle>
        <div style={{ display: "flex", gap: "15px", alignItems: "baseline" }}>
          <StyledTextField
            multiline
            size="small"
            margin="normal"
            variant="outlined"
            label="Comment"
            InputLabelProps={{ shrink: true }}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={disableForm}
          />

          <GlobalButton
            style={{ width: 160 }}
            name="saveComment"
            variant="contained"
            onClick={() => {
              let timeStamp = new Date().toLocaleString();

              let commentWithDate = `${username} (${timeStamp}):  ${newComment}`;
              let comment = myWorkRequest.comments
                ? commentWithDate + "|" + myWorkRequest.comments
                : commentWithDate;
              setMyWorkRequest({
                ...myWorkRequest,
                comments: comment,
              });
              setNewComment("");
              setIsRequiredComment(false);
            }}
            disabled={newComment === "" || !newComment || disableForm}>
            Save Comment
          </GlobalButton>
        </div>
        <div style={{ display: "flex" }}>
          <StyledTextField
            size="small"
            margin="normal"
            variant="outlined"
            label="Comments History"
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 500 }}
            multiline
            rows={4}
            value={myWorkRequest.comments ? mountComments(myWorkRequest.comments.split("|")) : ""}
            disabled={true}
          />
        </div>
      </>
    );
  };

  return (
    <div className="new-work-request-redesign">
      <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
        <span className="pageheader">
          {workRequestId ? `Request Details - ${myWorkRequest?.id}` : "Create Work Request"}
        </span>
        {workRequestId && workRequestFetched && featureToggle.CopyWorkRequestProcess && (
          <GlobalButton
            name="copy"
            variant="outlined"
            onClick={async () => {
              const payload = sanitizeWorkRequestForCreate(copyWorkRequest(workRequestFetched));
              const res = await WorkRequestApi.newCreateWorkRequest(payload);

              if (res?.message === "Success" && res.result?.id) {
                history.push("/workRequests");
                setTimeout(() => {
                  history.push(`/newWorkRequestRedesign/${res.result.id}`);
                }, 0);
              } else {
                openModalMessages(
                  "Fail",
                  `There was an error copying the work request. ${res?.message ?? ""}`,
                );
              }
            }}>
            Copy Work Request
          </GlobalButton>
        )}
      </div>

      <div className="container-buttons">
        {isProcess && (
          <GlobalButton
            className="action-button"
            name="btnPrint"
            variant="outlined"
            onClick={printRequest}>
            {instance.loading || workRequestLoading ? "Loading Print" : "Print Request"}
          </GlobalButton>
        )}
        {isProcess && showShipping && (
          <GlobalButton
            className="action-button"
            variant="outlined"
            sx={{ marginRight: "20px" }}
            onClick={handlePrintShippingForm}
            disabled={workRequestLoading}>
            Print Shipping Form
          </GlobalButton>
        )}
        {loadingSubmit && (
          <CircularProgress
            style={{
              textTransform: "none",
              marginTop: "50px",
              marginLeft: "15px",
            }}></CircularProgress>
        )}
        <GlobalButton
          className="action-button"
          name="btnSaveWorkRequest"
          variant="contained"
          onClick={() => submit()}
          disabled={loadingSubmit || disableForm}>
          Save All Changes
        </GlobalButton>
        {myWorkRequest.statusName === "Draft" && (
          <GlobalSecondaryButton
            className="action-button"
            name="btnSubmitWorkRequest"
            variant="contained"
            onClick={() => submit()}
            disabled={loadingSubmit || disableForm}>
            Submit Work Request
          </GlobalSecondaryButton>
        )}
      </div>

      <Box style={{ width: `calc(100vw - ${drawerWidth}px)`, marginTop: 10 }}>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="information-content"
            id="information-header">
            <Title>General Information</Title>
          </AccordionSummary>

          <AccordionDetails>
            <WorkRequestInformation
              sites={sites}
              sitesLoading={sitesLoading}
              requestFacilities={requestFacilities}
              requestFacilitiesLoading={requestFacilitiesLoading}
              requestTypes={requestTypes}
              requestTypesLoading={requestTypesLoading}
              // businessUnits={businessUnits}
              // businessUnitsLoading={businessUnitsLoading}
              workRequestPriority={workRequestPriority}
              workRequestPriorityLoading={workRequestPriorityLoading}
              errors={errors}
              setErrors={setErrors}
              myWorkRequest={myWorkRequest}
              setMyWorkRequest={setMyWorkRequest}
              setNonChemicalShipping={setNonChemicalShipping}
              requestStatus={requestStatus}
              requestStatusLoading={requestStatusLoading}
              isProcess={isProcess}
              disableForm={disableForm}
              workRequestFetched={workRequestFetched}
              shippingRequest={shippingRequest}
              setShippingRequest={setShippingRequest}
              errorsShipping={errorsShipping}
              setErrorsShipping={setErrorsShipping}
              mySamplesInfo={mySamplesInfo}
              setMySamplesInfo={setMySamplesInfo}
              setShippingAddress={setShippingAddress}
              workRequestTypeSiteMappingList={workRequestTypeSiteMappingList}
              availableLocations={availableLocations}
              currentUserEmail={username}
            />
          </AccordionDetails>
        </Accordion>
        {showContainers && (
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="containers-and-tests-content"
              id="containers-and-tests-header">
              <Title>Containers{withTests ? " and Tests" : ""}</Title>
            </AccordionSummary>

            <AccordionDetails>
              <ContainersAndTestsProcess
                isReadOnly={disableForm}
                mySamplesInfo={mySamplesInfo}
                setMySamplesInfo={setMySamplesInfo}
                unitOfMeasureLoading={unitOfMeasureLoading}
                unitOfMeasure={unitOfMeasure}
                shippingRequest={shippingRequest}
                setShippingRequest={setShippingRequest}
                workRequestId={workRequestId}
                setIsRequiredComment={setIsRequiredComment}
                isProcess={isProcess}
                availableLocations={availableLocations}
                prepareData={prepareData}
                myWorkRequest={myWorkRequest}
                setMyWorkRequest={setMyWorkRequest}
                workRequestFetched={workRequestFetched}
                withTests={withTests}
              />
            </AccordionDetails>
          </Accordion>
        )}
        {showShipping && (
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="shipping-content"
              id="shipping-header">
              <Title>Shipping</Title>
            </AccordionSummary>

            <AccordionDetails>
              <ShippingInformation
                shippingAddress={shippingAddress}
                setShippingAddress={setShippingAddress}
                shippingRequest={shippingRequest}
                setShippingRequest={setShippingRequest}
                setAddressBookOpen={setAddressBookOpen}
                errors={errorsShipping}
                setErrors={setErrorsShipping}
                shippingCarriersLoading={shippingCarriersLoading}
                shippingCarriers={shippingCarriers}
                shippingReasonsLoading={shippingReasonsLoading}
                shippingReasons={shippingReasons}
                nonChemicalShipping={nonChemicalShipping}
                setNonChemicalShipping={setNonChemicalShipping}
                myWorkRequest={myWorkRequest}
                unitOfMeasureWUnit={unitOfMeasureWUnit}
                unitOfMeasureLoading={unitOfMeasureLoading}
                mySamplesInfo={mySamplesInfo}
                dotHazardClass={dotHazardClass}
                dotHazardClassLoading={dotHazardClassLoading}
                workRequestId={workRequestId}
                isProcess={isProcess}
                disableForm={disableForm}
                files={files}
                setFiles={setFiles}
                openModalMessages={openModalMessages}
                isSiteToSiteTransport={isSiteToSiteTransport}
                sites={sites}
                sitesLoading={sitesLoading}
                checkShippingErrors={checkShippingErrors}
                workRequestTypeSiteMappingList={workRequestTypeSiteMappingList}
              />
              {addCommentComponent()}
              {showShipping && (
                <div style={{ marginTop: 10 }}>
                  {isShipping ? (
                    <b>
                      All packages are shipped via S&Rs discretion unless requested in the comment
                      box above. Please note if a specific shipping carrier, shipment method,
                      account number or payment method is required.
                    </b>
                  ) : (
                    <b>Your materials will be transported on the next available shuttle.</b>
                  )}
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        )}
        {isMiscellaneousRequest && (
          <WorkRequestFiles
            disableForm={disableForm}
            files={files}
            setFiles={setFiles}
            openModalMessages={openModalMessages}
          />
        )}
        {!showShipping && addCommentComponent()}
      </Box>

      <ModalSimpleButton
        title={modalMessagesTitle}
        buttonText={modalMessagesButtonText}
        buttonAction={closeModalMessages}
        open={modalMessagesOpen}
        setOpen={setModalMessagesOpen}>
        <div style={{ minWidth: "300px" }}>
          <label style={{ whiteSpace: "break-spaces" }}>{modalMessagesText}</label>
        </div>
      </ModalSimpleButton>

      <ModalMethodFacilityExternal
        facilitiesExternal={facilitiesExternalToShow}
        modalOpen={modalFacilityExternalOpen}
        setModalOpen={setModalFacilityExternalOpen}
        setModalClose={closeModalFacilityExternal}
        created={true}
      />

      {addressBookOpen && (
        <AddressBook
          open={addressBookOpen}
          setOpen={setAddressBookOpen}
          handleApply={handleAddressBook}
        />
      )}

      <ShippingPrint
        shippingAddress={shippingAddress}
        shippingRequest={shippingRequest}
        shippingItems={shippingRequest?.shippingItems}
        shippingCarriers={shippingCarriers}
        isTSCA={shippingRequest?.isTSCAReview}
      />
    </div>
  );
};

export default ProcessWorkRequest;
